import React, { useMemo, useState } from 'react';
/**
 * RulebookHintsPanel — Family-hints verkenner (P1-4).
 *
 * Doel: één plek waar een redacteur per family ziet wat de rulebooks
 * voorschrijven aan vraagtypes (familyTypeHints) én visuals
 * (familyVisualHints). Dit is de inverse van LiveRulebookCard, dat per
 * module toont. Hier kies je de family en ziet je alle matchende hints
 * vanuit elk relevant rulebook — een 1-blik check voor
 * "past mijn vraag bij deze family?".
 *
 * Gevoed door:
 *   - QUESTIONS_BY_FAMILY (alle bestaande families)
 *   - resolveFamilyTypeHints() / resolveFamilyVisualHints() (regelboeken)
 *   - getModuleRulebook() voor de module-context van de geselecteerde family
 *   - FAMILY_DISPLAY_NAMES voor mensvriendelijke labels
 */
import { TypeIcon, PaletteIcon, CheckCircle2Icon, XCircleIcon, SearchIcon, FilterIcon, InfoIcon } from 'lucide-react';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { QUESTIONS_BY_FAMILY, FAMILY_DISPLAY_NAMES } from '../../../../content';
import { resolveFamilyTypeHints, resolveFamilyVisualHints, getModuleRulebook } from '../../../../governance';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
type FamilyRow = {
  familyId: string;
  displayName: string;
  shortCode: string;
  moduleName: string;
  questionCount: number;
  hasTypeHint: boolean;
  hasVisualHint: boolean;
};
export function RulebookHintsPanel() {
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [hintFilter, setHintFilter] = useState<'all' | 'with-type' | 'with-visual' | 'without-any'>('all');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  // Build the family rows once — we resolve hints upfront so we can
  // filter "with/without hint" without recomputing on each render.
  const rows = useMemo<FamilyRow[]>(() => {
    const out: FamilyRow[] = [];
    for (const [familyId, questions] of QUESTIONS_BY_FAMILY.entries()) {
      const shortCode = familyId.split('-')[0] || 'UNK';
      const meta = ADMIN_MODULE_META[shortCode];
      const typeHints = resolveFamilyTypeHints(familyId);
      const visualHints = resolveFamilyVisualHints(familyId);
      out.push({
        familyId,
        displayName: FAMILY_DISPLAY_NAMES[familyId] ?? familyId,
        shortCode,
        moduleName: meta?.name ?? shortCode,
        questionCount: questions.length,
        hasTypeHint: typeHints.length > 0,
        hasVisualHint: visualHints.length > 0
      });
    }
    return out.sort((a, b) => a.familyId.localeCompare(b.familyId));
  }, []);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (moduleFilter !== 'all' && r.shortCode !== moduleFilter) return false;
      if (hintFilter === 'with-type' && !r.hasTypeHint) return false;
      if (hintFilter === 'with-visual' && !r.hasVisualHint) return false;
      if (hintFilter === 'without-any' && (r.hasTypeHint || r.hasVisualHint)) return false;
      if (q !== '') {
        const hay = r.familyId.toLowerCase() + ' ' + r.displayName.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, moduleFilter, hintFilter]);
  // Auto-select first family on first render to avoid empty detail panel.
  const effectiveSelected = selectedFamilyId ?? filteredRows[0]?.familyId ?? null;
  const stats = useMemo(() => {
    const withType = rows.filter(r => r.hasTypeHint).length;
    const withVisual = rows.filter(r => r.hasVisualHint).length;
    const withAny = rows.filter(r => r.hasTypeHint || r.hasVisualHint).length;
    return {
      total: rows.length,
      withType,
      withVisual,
      withAny,
      coverage: rows.length === 0 ? 0 : Math.round(withAny / rows.length * 100)
    };
  }, [rows]);
  return <div className="space-y-5" data-id="element-4888" data-mp="27yt2t10gaenv">
      {/* Header card */}
      <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4" data-id="element-4889" data-mp="281fcp7atd699">
        <p className="text-sm text-indigo-900" data-id="element-4890" data-mp="3gwsw0odw84vs">
          <strong data-id="element-4891" data-mp="3bxzgyriyk5ti">Family-hints verkenner</strong> — kies een family om te zien
          welke vraagtypes en visuals de regelboeken voorschrijven of afraden.
          Komt rechtstreeks uit{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4892" data-mp="3kye8i8mimh5g">
            familyTypeHints
          </code>{' '}
          en{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4893" data-mp="3opu96te6his">
            familyVisualHints
          </code>{' '}
          in{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4894" data-mp="3uu7qw8mx14v5">
            src/governance/rulebooks/
          </code>
          .
        </p>
      </div>

      {/* Coverage tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-4895" data-mp="8hizdvsrvrve">
        <CoverageTile label="Families totaal" value={stats.total} accent="slate" data-id="element-4896" data-mp="mfpfpy1oh97b" />
        
        <CoverageTile label="Met type-hint" value={stats.withType} accent="purple" data-id="element-4897" data-mp="hhxjwsxwx2xu" />
        
        <CoverageTile label="Met visual-hint" value={stats.withVisual} accent="emerald" data-id="element-4898" data-mp="112o41k58nnht" />
        
        <CoverageTile label="Dekking" value={`${stats.coverage}%`} accent="indigo" data-id="element-4899" data-mp="162tzlbempu56" />
        
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row" data-id="element-4900" data-mp="r489t8awzy43">
        <div className="flex-1" data-id="element-4901" data-mp="w3untjykhidy">
          <Input type="text" placeholder="Zoek op family ID of naam..." value={search} onChange={e => {
          setSearch(e.target.value);
          setSelectedFamilyId(null);
        }} icon={<SearchIcon className="w-4 h-4" data-id="element-4903" data-mp="n3oe895zp2k2" />} variant="admin" size="sm" data-id="element-4902" data-mp="w334p8m5pd4k" />
          
        </div>
        <Select value={moduleFilter} onChange={e => {
        setModuleFilter(e.target.value);
        setSelectedFamilyId(null);
      }} options={[{
        value: 'all',
        label: 'Alle vakken'
      }, ...Object.entries(ADMIN_MODULE_META).map(([code, meta]) => ({
        value: code,
        label: `${meta.name} (${code})`
      }))]} variant="admin" size="sm" fullWidth={false} data-id="element-4904" data-mp="8f1xq44mm4r9" />
        
        <Select value={hintFilter} onChange={e => {
        setHintFilter(e.target.value as typeof hintFilter);
        setSelectedFamilyId(null);
      }} options={[{
        value: 'all',
        label: 'Alle families'
      }, {
        value: 'with-type',
        label: 'Met type-hint'
      }, {
        value: 'with-visual',
        label: 'Met visual-hint'
      }, {
        value: 'without-any',
        label: 'Zonder hint'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-4905" data-mp="262dy32zjgwz9" />
        
      </div>

      <p className="text-sm text-gray-500 flex items-center gap-2" data-id="element-4906" data-mp="2k2x4y6s9pwzu">
        <FilterIcon className="h-3.5 w-3.5" data-id="element-4907" data-mp="2f2iuyj73wcz6" />
        {filteredRows.length} van {rows.length} families
      </p>

      {/* Two-pane layout: family list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-id="element-4908" data-mp="2b3a0fc48sb2k">
        {/* Family list */}
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden max-h-[640px] overflow-y-auto" data-id="element-4909" data-mp="13lj1vlcxpu43">
          {filteredRows.length === 0 ? <div className="py-12 text-center text-sm text-gray-500" data-id="element-4910" data-mp="3b9x8b7w3hddu">
              <SearchIcon className="mx-auto h-7 w-7 text-gray-300 mb-2" data-id="element-4911" data-mp="3faz0j4rlgp6b" />
              Geen families gevonden.
            </div> : <ul role="list" data-id="element-4912" data-mp="3k9k2b8mhvgwv">
              {filteredRows.map(row => {
            const isSelected = row.familyId === effectiveSelected;
            return <li key={row.familyId} data-id="element-4913" data-mp="3p2d4ppuhqd5s">
                    <button onClick={() => setSelectedFamilyId(row.familyId)} className={`w-full text-left px-3 py-2.5 border-b border-gray-50 transition-colors ${isSelected ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'}`} data-id="element-4914" data-mp="3tzvflls2j2gf">
                    
                      <div className="flex items-center justify-between gap-2" data-id="element-4915" data-mp="bu0cykoy2in3">
                        <code className={`text-xs font-mono font-semibold truncate ${isSelected ? 'text-indigo-700' : 'text-gray-800'}`} data-id="element-4916" data-mp="brwc6bmcdzr1">
                        
                          {row.familyId}
                        </code>
                        <div className="flex gap-1 flex-shrink-0" data-id="element-4917" data-mp="jd9p2lou4vfu">
                          {row.hasTypeHint && <span title="Heeft type-hint" className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500" data-id="element-4918" data-mp="fcg1jtfb1inh" />}
                          {row.hasVisualHint && <span title="Heeft visual-hint" className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4919" data-mp="t32z3i9bx2hz" />}
                        </div>
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${isSelected ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-4920" data-mp="fymos7wplw6">
                      
                        {row.displayName}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-4921" data-mp="adw9rce15cn7">
                        {row.shortCode} · {row.questionCount} vragen
                      </p>
                    </button>
                  </li>;
          })}
            </ul>}
        </div>

        {/* Detail panel */}
        <div className="min-h-[400px]" data-id="element-4922" data-mp="1gs3yyh43j5bn">
          {effectiveSelected ? <FamilyHintsDetail familyId={effectiveSelected} data-id="element-4923" data-mp="2yqs8j5uen7zm" /> : <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center" data-id="element-4924" data-mp="2yq0omsa66yp6">
              <InfoIcon className="mx-auto h-7 w-7 text-gray-300 mb-2" data-id="element-4925" data-mp="33rg7qessev4x" />
              <p className="text-sm text-gray-500" data-id="element-4926" data-mp="2pr5ghankfej8">
                Selecteer een family om de hints te zien.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}
// ─── Detail panel ───────────────────────────────────────────────────────────
function FamilyHintsDetail({
  familyId
}: {
  familyId: string;
}) {
  const typeHints = resolveFamilyTypeHints(familyId);
  const visualHints = resolveFamilyVisualHints(familyId);
  const shortCode = familyId.split('-')[0] || '';
  const moduleMeta = ADMIN_MODULE_META[shortCode];
  // Find parent rulebook for module-level preferred types/visuals as fallback.
  const parentModuleId = typeHints[0]?.moduleId ?? visualHints[0]?.moduleId;
  const parentRulebook = parentModuleId ? getModuleRulebook(parentModuleId) : null;
  return <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4927" data-mp="32lciu2ug1coh">
      {/* Family header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white" data-id="element-4928" data-mp="37jnsacgttl66">
        <div className="flex items-center gap-2 mb-1" data-id="element-4929" data-mp="2tjw81tzpv8jq">
          <code className="text-base font-mono font-bold text-gray-900" data-id="element-4930" data-mp="2tnadjb3ofxve">
            {familyId}
          </code>
          {moduleMeta && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${moduleMeta.color}`} data-id="element-4931" data-mp="2jumiaksci1ys">
            
              {shortCode}
            </span>}
        </div>
        <p className="text-sm text-gray-600" data-id="element-4932" data-mp="29x8crg7ymxl9">
          {FAMILY_DISPLAY_NAMES[familyId] ?? '—'}
        </p>
        {parentRulebook && <p className="text-xs text-gray-500 mt-1.5" data-id="element-4933" data-mp="2ex32vhjwvj59">
            Rulebook: <strong data-id="element-4934" data-mp="20wa8cwtm0o25">{parentRulebook.displayName}</strong> ·{' '}
            {parentRulebook.didactics.coreSummary}
          </p>}
      </div>

      <div className="p-5 space-y-5" data-id="element-4935" data-mp="25ubpvdbe0fbi">
        {/* Type hints */}
        <Section icon={<TypeIcon className="h-4 w-4 text-purple-600" data-id="element-4937" data-mp="1m8rx1fljkv6r" />} title="Vraagtype-hints" count={typeHints.length} data-id="element-4936" data-mp="1m9bdervjnbio">
          
          {typeHints.length === 0 ? <EmptyHint kind="vraagtype" data-id="element-4938" data-mp="1hcbcevdjwyyf" /> : <ul className="space-y-3" data-id="element-4939" data-mp="1v9zuxwk5bu0b">
              {typeHints.map((hint, i) => <li key={`${hint.moduleId}-${i}`} className="rounded-lg border border-purple-100 bg-purple-50/40 p-3" data-id="element-4940" data-mp="1v8582cpbdrtl" data-mpi={i}>
              
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-4941" data-mp="2585w2by4ow44">
                    <code className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded border border-purple-100 text-purple-700" data-id="element-4942" data-mp="2562x32ezzsn9">
                      {hint.familyIdPattern}
                    </code>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide" data-id="element-4943" data-mp="29z5rtdvih851">
                      {hint.moduleId}
                    </span>
                  </div>
                  {hint.preferredTypes && hint.preferredTypes.length > 0 && <ChipRow label="Voorkeur" labelIcon={<CheckCircle2Icon className="h-3 w-3 text-emerald-600" data-id="element-4945" data-mp="2j1nd9q3cdwqz" />} chips={hint.preferredTypes} chipClass="bg-emerald-50 text-emerald-700 border-emerald-100" data-id="element-4944" data-mp="2j0lsgtwixvw2" />}
                  {hint.discouragedTypes && hint.discouragedTypes.length > 0 && <ChipRow label="Afgeraden" labelIcon={<XCircleIcon className="h-3 w-3 text-red-500" data-id="element-4947" data-mp="1pm1hibgrh641" />} chips={hint.discouragedTypes} chipClass="bg-red-50 text-red-700 border-red-100 line-through decoration-red-300" data-id="element-4946" data-mp="1pn49kmptlelj" />}
                  <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-4948" data-mp="1zdffufwz7zvl">
                    {hint.note}
                  </p>
                </li>)}
            </ul>}
        </Section>

        {/* Visual hints */}
        <Section icon={<PaletteIcon className="h-4 w-4 text-emerald-600" data-id="element-4950" data-mp="29bld2k7u8hu4" />} title="Visual-hints" count={visualHints.length} data-id="element-4949" data-mp="29c4y49ssbmcd">
          
          {visualHints.length === 0 ? <EmptyHint kind="visual" data-id="element-4951" data-mp="exc0y6c96nib" /> : <ul className="space-y-3" data-id="element-4952" data-mp="ey3z9ox8izca">
              {visualHints.map((hint, i) => <li key={`${hint.moduleId}-${i}`} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-id="element-4953" data-mp="iye2wuxk1dnz" data-mpi={i}>
              
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-4954" data-mp="bfwqogyankrb">
                    <code className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded border border-emerald-100 text-emerald-700" data-id="element-4955" data-mp="6o6b9wki26t1">
                      {hint.familyIdPattern}
                    </code>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide" data-id="element-4956" data-mp="3ttxcghdjxpuj">
                      {hint.moduleId}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1.5" data-id="element-4957" data-mp="pf5wcmoviwh1">
                    {hint.preferredStimulus && <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded" data-id="element-4958" data-mp="ld2gzmz337qh">
                        <span className="text-[9px] uppercase tracking-wider text-emerald-600" data-id="element-4959" data-mp="gdptr14avynd">
                          Stimulus
                        </span>
                        <code className="font-mono text-[11px]" data-id="element-4960" data-mp="313xaorecksvw">
                          {hint.preferredStimulus}
                        </code>
                      </span>}
                    {hint.preferredSupport && <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded" data-id="element-4961" data-mp="xn0gdj6wqf68">
                        <span className="text-[9px] uppercase tracking-wider text-emerald-600" data-id="element-4962" data-mp="jszierns4w6x">
                          Support
                        </span>
                        <code className="font-mono text-[11px]" data-id="element-4963" data-mp="1bnbdv52q2s0d">
                          {hint.preferredSupport}
                        </code>
                      </span>}
                  </div>
                  {hint.questionTypeExempt && hint.questionTypeExempt.length > 0 && <p className="text-[11px] text-gray-500 italic mb-1" data-id="element-4964" data-mp="17kn4mwa3jj2v">
                        Uitgezonderd voor:{' '}
                        {hint.questionTypeExempt.map((t, j) => <code key={j} className="font-mono bg-gray-100 px-1 rounded mr-1" data-id="element-4965" data-mp="3nahn4q899n34" data-mpi={j}>
                  
                            {t}
                          </code>)}
                      </p>}
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-4966" data-mp="3js23lvwr5d9l">
                    {hint.note}
                  </p>
                </li>)}
            </ul>}
        </Section>

        {/* Module-level fallback */}
        {parentRulebook && <div className="rounded-lg border border-gray-100 bg-gray-50 p-3" data-id="element-4967" data-mp="3ot7wpuubr2jn">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2 flex items-center gap-1.5" data-id="element-4968" data-mp="3oqvkzzf5tof1">
              <InfoIcon className="h-3 w-3" data-id="element-4969" data-mp="2w4kk0nnn5lhp" />
              Module-fallback ({parentRulebook.displayName})
            </p>
            <div className="space-y-2 text-xs" data-id="element-4970" data-mp="3atglu2z3u29v">
              {parentRulebook.questionTypes.preferredTypes.length > 0 && <div className="flex flex-wrap gap-1.5 items-center" data-id="element-4971" data-mp="361fdgs524vp4">
                  <span className="text-gray-500" data-id="element-4972" data-mp="2icmcuidhpzo6">Voorkeur vraagtypes:</span>
                  {parentRulebook.questionTypes.preferredTypes.map(t => <code key={t} className="font-mono bg-white border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px]" data-id="element-4973" data-mp="2dd02qhsykzjl">
                
                      {t}
                    </code>)}
                </div>}
              {parentRulebook.visuals.preferredAidTypes && parentRulebook.visuals.preferredAidTypes.length > 0 && <div className="flex flex-wrap gap-1.5 items-center" data-id="element-4974" data-mp="r19fhn8qw791">
                    <span className="text-gray-500" data-id="element-4975" data-mp="v0rrftil97zq">Voorkeur visuals:</span>
                    {parentRulebook.visuals.preferredAidTypes.map(v => <code key={v} className="font-mono bg-white border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px]" data-id="element-4976" data-mp="3uj9ienzg1mfs">
                
                        {v}
                      </code>)}
                  </div>}
            </div>
          </div>}
      </div>
    </div>;
}
// ─── Small UI primitives ────────────────────────────────────────────────────
function Section({
  icon,
  title,
  count,
  children
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return <div data-id="element-4977" data-mp="1xunq3ytz5ma0">
      <div className="flex items-center gap-2 mb-2" data-id="element-4978" data-mp="1xudyk6ta7727">
        {icon}
        <h4 className="text-sm font-semibold text-gray-800" data-id="element-4979" data-mp="1xwrx4pn671ei">{title}</h4>
        <span className="text-xs text-gray-400 font-medium" data-id="element-4980" data-mp="1sw4643yxykka">({count})</span>
      </div>
      {children}
    </div>;
}
function ChipRow({
  label,
  labelIcon,
  chips,
  chipClass
}: {
  label: string;
  labelIcon: React.ReactNode;
  chips: string[];
  chipClass: string;
}) {
  return <div className="flex flex-wrap items-center gap-1.5 mb-1" data-id="element-4981" data-mp="1f57did50m3d8">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold" data-id="element-4982" data-mp="1a52zctrkxkq6">
        {labelIcon}
        {label}
      </span>
      {chips.map(c => <code key={c} className={`font-mono text-[11px] px-1.5 py-0.5 rounded border ${chipClass}`} data-id="element-4983" data-mp="157t95bwd951a">
        
          {c}
        </code>)}
    </div>;
}
function EmptyHint({
  kind
}: {
  kind: 'vraagtype' | 'visual';
}) {
  return <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center" data-id="element-4984" data-mp="2vhw8vm2lch8b">
      <p className="text-xs text-gray-500" data-id="element-4985" data-mp="3eqnqfojtx06x">
        Geen {kind}-hint gedefinieerd voor deze family.
      </p>
      <p className="text-[11px] text-gray-400 mt-1" data-id="element-4986" data-mp="3it0vsbunyw36">
        De module-fallback hieronder geldt.
      </p>
    </div>;
}
function CoverageTile({
  label,
  value,
  accent
}: {
  label: string;
  value: number | string;
  accent: 'slate' | 'purple' | 'emerald' | 'indigo';
}) {
  const map = {
    slate: 'bg-slate-50 border-slate-100 text-slate-700',
    purple: 'bg-purple-50 border-purple-100 text-purple-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700'
  } as const;
  return <div className={`rounded-lg border p-3 text-center ${map[accent]}`} data-id="element-4987" data-mp="2m4uu9xt1gjt4">
      <p className="text-2xl font-bold" data-id="element-4988" data-mp="2r24qbwaxntpx">{value}</p>
      <p className="text-xs font-medium opacity-80" data-id="element-4989" data-mp="kaltrr2p3zbs">{label}</p>
    </div>;
}